import type {
  AIActivity,
  ChatMessage,
  EnhancedFileCitation,
  FileCitation,
} from '../types/chat.types';
import type { PredefinedPromptProps } from '../types/predefinedPrompts';
import { streamingRegistry } from './streamingRegistry';
import { createAgentMessage, createAbortMessage } from './messageFactory';
import {
  enhanceMessageCitations,
  updateMessageCitations,
} from './citationEnhancement';
import { composeAnnotatedContent } from './citations/annotationComposer';
import { createEnrichmentScheduler } from './citations/enrichmentScheduler';

interface StreamExecutorParams {
  messageContent: string;
  conversationId?: string;
  agentMessageId: string;
  lastResponseId: string | null;
  predefinedPromptProps?: PredefinedPromptProps;
  streamMessage: (
    content: string,
    onChunk: (chunk: string) => void,
    onActivity: (activity: AIActivity) => void,
    onCitation: (citation: Partial<FileCitation>) => void,
    responseId?: string,
    predefinedPromptProps?: PredefinedPromptProps,
    onReasoningSummary?: (delta: string) => void,
    conversationId?: string,
    onConversationId?: (conversationId: string) => void,
  ) => Promise<string | null>;
  setError: (error: string | null) => void;
  enhanceCitations: (
    citations: EnhancedFileCitation[],
  ) => Promise<EnhancedFileCitation[]>;
}

interface StreamExecutorResult {
  success: boolean;
  message: ChatMessage | null;
  responseId: string | null;
  conversationId: string | null;
}

type MinimalCitation = FileCitation & {
  file_id: string;
  filename: string;
};

const isValidCitation = (
  citation: Partial<FileCitation>,
): citation is MinimalCitation => {
  return (
    citation.type === 'file_citation' &&
    Boolean(citation.file_id && citation.filename) &&
    Number.isFinite(citation.index)
  );
};

const uniqueByFileId = (
  citations: EnhancedFileCitation[],
): EnhancedFileCitation[] => {
  const map = new Map<string, EnhancedFileCitation>();

  citations.forEach(citation => {
    if (!map.has(citation.file_id)) {
      map.set(citation.file_id, citation);
    }
  });

  return Array.from(map.values());
};

const upsertLiveCitation = (
  citations: EnhancedFileCitation[],
  citation: EnhancedFileCitation,
): EnhancedFileCitation[] => {
  const keyOf = (entry: EnhancedFileCitation): string =>
    [entry.file_id, entry.index ?? 0, entry.annotation_index ?? ''].join(':');

  const key = keyOf(citation);
  const existingIndex = citations.findIndex(existing => {
    return keyOf(existing) === key;
  });

  if (existingIndex === -1) {
    return [...citations, citation];
  }

  const next = [...citations];
  next[existingIndex] = {
    ...next[existingIndex],
    ...citation,
  };
  return next;
};

export const executeStreaming = async (
  params: StreamExecutorParams,
): Promise<StreamExecutorResult> => {
  const {
    messageContent,
    conversationId,
    agentMessageId,
    lastResponseId,
    predefinedPromptProps,
    streamMessage,
    setError,
    enhanceCitations,
  } = params;

  let rawText = '';
  let liveCitations: EnhancedFileCitation[] = [];
  let capturedResponseId: string | null = null;
  let capturedConversationId: string | null = conversationId ?? null;

  const renderStreamingState = (finalize = false) => {
    const composed = composeAnnotatedContent(rawText, liveCitations, {
      finalize,
    });
    const citationsForRender = uniqueByFileId(composed.resolvedCitations);
    streamingRegistry.updateContent(agentMessageId, composed.content);
    streamingRegistry.updateCitations(agentMessageId, citationsForRender);
    return {
      content: composed.content,
      citations: citationsForRender,
    };
  };

  const mergeEnhancedCitations = (enhanced: EnhancedFileCitation[]) => {
    const enhancedByFileId = new Map(
      enhanced.map(citation => [citation.file_id, citation]),
    );

    liveCitations = liveCitations.map(citation => {
      const match = enhancedByFileId.get(citation.file_id);
      if (!match) {
        return citation;
      }

      return {
        ...citation,
        ...match,
      };
    });
  };

  const enrichmentScheduler = createEnrichmentScheduler({
    enhanceCitations,
    getPendingCitations: fileIds =>
      uniqueByFileId(
        liveCitations.filter(citation => fileIds.has(citation.file_id)),
      ),
    mergeEnhancedCitations,
    onStateUpdated: () => {
      renderStreamingState(false);
    },
  });

  let reasoningSummary = '';

  try {
    capturedResponseId = await streamMessage(
      messageContent,
      (chunk: string) => {
        rawText += chunk;
        renderStreamingState(false);
      },
      activity => {
        // Add a line break between different agents' reasoning summaries
        if (activity !== 'reasoning' && reasoningSummary) {
          reasoningSummary += `\n\n`;
        }
        streamingRegistry.updateActivity(agentMessageId, activity);
      },
      citation => {
        if (!isValidCitation(citation)) return;

        const citationWithId: EnhancedFileCitation = {
          ...citation,
          type: 'file_citation',
          index: citation.index ?? 0,
        };
        liveCitations = upsertLiveCitation(liveCitations, citationWithId);
        enrichmentScheduler.schedule(citationWithId.file_id);
        renderStreamingState(false);
      },
      lastResponseId || undefined,
      predefinedPromptProps,
      (delta: string) => {
        reasoningSummary += delta;
        streamingRegistry.updateReasoningSummary(
          agentMessageId,
          reasoningSummary,
        );
      },
      conversationId,
      backendConversationId => {
        capturedConversationId = backendConversationId;
      },
    );

    enrichmentScheduler.cancel();

    const finalRender = renderStreamingState(true);
    streamingRegistry.complete(
      agentMessageId,
      finalRender.content,
      finalRender.citations,
    );

    return {
      success: true,
      message: createAgentMessage(
        agentMessageId,
        finalRender.content,
        finalRender.citations,
        capturedResponseId,
        reasoningSummary.trim() || undefined,
      ),
      responseId: capturedResponseId,
      conversationId: capturedConversationId,
    };
  } catch (error: unknown) {
    enrichmentScheduler.cancel();

    const isAbortError = error instanceof Error && error.name === 'AbortError';

    if (!isAbortError) {
      setError(
        error instanceof Error ? error.message : 'An unexpected error occurred',
      );
      streamingRegistry.error(
        agentMessageId,
        error instanceof Error ? error : new Error('Streaming failed'),
      );
    }

    if (isAbortError) {
      /**
       * On abort, do NOT return the capturedResponseId. Returning null ensures
       * we don't accidentally promote an incomplete ID to lastGoodResponseId.
       */
      return {
        success: true,
        message: createAbortMessage(agentMessageId),
        responseId: null,
        conversationId: capturedConversationId,
      };
    }

    return {
      success: false,
      message: null,
      responseId: null,
      conversationId: capturedConversationId,
    };
  }
};

export const enhanceAndUpdateCitations = async (
  message: ChatMessage,
  enhanceCitations: (
    citations: EnhancedFileCitation[],
  ) => Promise<EnhancedFileCitation[]>,
  setCompletedMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
): Promise<void> => {
  const enhancedMessage = await enhanceMessageCitations(
    message,
    enhanceCitations,
  );

  if (enhancedMessage !== message) {
    setCompletedMessages(prev =>
      updateMessageCitations(prev, message.id, enhancedMessage),
    );
  }
};
